import { Popover, Transition } from '@headlessui/react';
import { CSSProperties, memo, useCallback, useMemo } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  getSpeakerLabelBorderRadiusFull,
  getSpeakerLabelBorderRadiusDefault
} from '@goldcast/player-config/src/utils/speakerLabelUtils';
import { BACKGROUND_COLOR_DEFAULT, TEXT_COLOR_DEFAULT } from '@goldcast/player-config/src/constants/colors';
import SpeakerLabelShapeSelector from './SpeakerLabelAppearanceMenu/SpeakerLabelShapeSelector';
import SpeakerLabelColorPicker from './SpeakerLabelAppearanceMenu/SpeakerLabelColorPicker';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import { updateSpeakerLabelStyles } from '@/stores/clip';
import Icon from '@/components/atoms/Icon';

function SpeakerLabelAppearanceMenu({ clip }: { clip: Clip }) {
  const isAudiogram = useMemo(() => clip.asset_metadata.layout === 'AUDIOGRAM', [clip.asset_metadata.layout]);

  // Get current styles - wrapped in useMemo to prevent callback dependency changes
  const containerStyle = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.containerStyle || {},
    [clip.asset_metadata.speaker_label_styles?.containerStyle]
  );
  const nameStyle = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.nameStyle || {},
    [clip.asset_metadata.speaker_label_styles?.nameStyle]
  );
  const titleStyle = useMemo(
    () => clip.asset_metadata.speaker_label_styles?.titleStyle || {},
    [clip.asset_metadata.speaker_label_styles?.titleStyle]
  );

  const currentShape = useMemo(() => {
    if (containerStyle.backgroundColor === 'transparent') {
      return 'none';
    }

    const borderRadius = containerStyle.borderRadius as string;

    if (!borderRadius || borderRadius === '0' || borderRadius === '0px') {
      return 'rectangle';
    }

    if (borderRadius === getSpeakerLabelBorderRadiusFull()) {
      return 'pill';
    }

    return 'rounded';
  }, [containerStyle.borderRadius, containerStyle.backgroundColor]);

  const backgroundColor = useMemo(
    () =>
      (containerStyle.backgroundColor as string) ||
      clip.asset_metadata.speaker_label_styles?.background_color ||
      BACKGROUND_COLOR_DEFAULT,
    [containerStyle.backgroundColor, clip.asset_metadata.speaker_label_styles?.background_color]
  );

  // Effective background color excludes 'transparent' to remember previous color
  const effectiveBackgroundColor = useMemo(() => {
    const currentBg = containerStyle.backgroundColor as string;
    if (currentBg && currentBg !== 'transparent') {
      return currentBg;
    }
    const legacyBg = clip.asset_metadata.speaker_label_styles?.background_color;
    if (legacyBg && legacyBg !== 'transparent') {
      return legacyBg;
    }
    return BACKGROUND_COLOR_DEFAULT;
  }, [containerStyle.backgroundColor, clip.asset_metadata.speaker_label_styles?.background_color]);

  const nameColor = useMemo(
    () => (nameStyle.color as string) || clip.asset_metadata.speaker_label_styles?.text_color || TEXT_COLOR_DEFAULT,
    [nameStyle.color, clip.asset_metadata.speaker_label_styles?.text_color]
  );

  const titleColor = useMemo(
    () => (titleStyle.color as string) || clip.asset_metadata.speaker_label_styles?.text_color || TEXT_COLOR_DEFAULT,
    [titleStyle.color, clip.asset_metadata.speaker_label_styles?.text_color]
  );

  // Update functions
  const updateContainerStyle = useCallback(
    (updates: CSSProperties) => {
      updateSpeakerLabelStyles(clip.id, {
        containerStyle: { ...containerStyle, ...updates }
      });
    },
    [clip.id, containerStyle]
  );

  const updateNameStyle = useCallback(
    (updates: CSSProperties) => {
      updateSpeakerLabelStyles(clip.id, {
        nameStyle: { ...nameStyle, ...updates }
      });
    },
    [clip.id, nameStyle]
  );

  const updateTitleStyle = useCallback(
    (updates: CSSProperties) => {
      updateSpeakerLabelStyles(clip.id, {
        titleStyle: { ...titleStyle, ...updates }
      });
    },
    [clip.id, titleStyle]
  );

  const onShapeChange = useCallback(
    (shape: string) => {
      switch (shape) {
        case 'rounded':
          updateContainerStyle({
            borderRadius: getSpeakerLabelBorderRadiusDefault(),
            backgroundColor: effectiveBackgroundColor,
            padding: '1em 1.5em'
          });
          break;
        case 'rectangle':
          updateContainerStyle({
            borderRadius: undefined,
            backgroundColor: effectiveBackgroundColor,
            padding: '1em 1.5em'
          });
          break;
        case 'pill':
          updateContainerStyle({
            borderRadius: getSpeakerLabelBorderRadiusFull(),
            backgroundColor: effectiveBackgroundColor,
            padding: '1em 2.5em'
          });
          break;
        case 'none':
          updateContainerStyle({ backgroundColor: 'transparent' });
          break;
      }
    },
    [updateContainerStyle, effectiveBackgroundColor]
  );

  const onBackgroundColorChanged = useCallback(
    (value?: string) => {
      updateContainerStyle({ backgroundColor: value });
      // Also update legacy field for backward compatibility
      updateSpeakerLabelStyles(clip.id, { background_color: value });
    },
    [clip.id, updateContainerStyle]
  );

  const onNameColorChanged = useCallback(
    (value?: string) => {
      updateNameStyle({ color: value });
      // Also update legacy field for backward compatibility
      updateSpeakerLabelStyles(clip.id, { text_color: value });
    },
    [clip.id, updateNameStyle]
  );

  const onTitleColorChanged = useCallback(
    (value?: string) => {
      updateTitleStyle({ color: value });
    },
    [updateTitleStyle]
  );

  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="speaker-label-appearance-button"
            className="group/button flex size-10 select-none items-center justify-center rounded-md hover:bg-slate-100"
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconPalette" size={20} />
              <PlayerMenuTooltip content="Appearance" />
            </div>
          </Popover.Button>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="flex select-none justify-start">
              <div className="absolute -right-24 mt-1.5 flex w-60 flex-col divide-y rounded-xl border border-slate-200 bg-white text-sm shadow-lg">
                <div className="p-2.5">
                  <SpeakerLabelShapeSelector value={currentShape} onChange={onShapeChange} />
                </div>
                <div className="px-2.5 py-1.5">
                  <SpeakerLabelColorPicker
                    label="Name Color"
                    value={nameColor}
                    onChange={onNameColorChanged}
                    testId="name-color-picker"
                  />
                </div>
                <div className="px-2.5 py-1.5">
                  <SpeakerLabelColorPicker
                    label="Title Color"
                    value={titleColor}
                    onChange={onTitleColorChanged}
                    testId="title-color-picker"
                  />
                </div>
                {!isAudiogram && currentShape !== 'none' && (
                  <div className="px-2.5 py-1.5">
                    <SpeakerLabelColorPicker
                      label="Background"
                      value={backgroundColor}
                      onChange={onBackgroundColorChanged}
                      testId="background-color-picker"
                    />
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(SpeakerLabelAppearanceMenu);
